import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, TextField } from '@mui/material';
import { LockResetOutlined as LockIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { apiClient } from '../../services/api';
import { ApiError } from '../../services/api/http';
import { useLanguage } from '../../contexts/LanguageContext';
import AuthPageShell from './AuthPageShell';

const ResetPassword: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (!token) {
      setError(t('auth.invalidPasswordResetLink'));
      return;
    }
    if (password.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }
    if (password !== confirmation) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiClient.resetPassword(token, password);
      navigate('/login', { replace: true, state: { message: response.message || t('auth.passwordResetSuccess') } });
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : t('auth.passwordResetFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageShell title={t('auth.resetPassword')} description={t('auth.resetPasswordDescription')}
      icon={<LockIcon />} backTo="/login" backLabel={t('auth.backToLogin')}>
      {!token && <Alert severity="error" sx={{ mb: 2 }}>{t('auth.invalidPasswordResetLink')}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {token ? (
        <Box component="form" onSubmit={submit} noValidate sx={{ display: 'grid', gap: 2 }}>
          <TextField fullWidth type={showPassword ? 'text' : 'password'} label={t('auth.newPassword')}
            autoComplete="new-password" value={password} onChange={event => { setPassword(event.target.value); setError(''); }}
            disabled={isLoading} required helperText={t('auth.passwordMinLength')}
            InputProps={{ endAdornment: <InputAdornment position="end"><IconButton
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              onClick={() => setShowPassword(value => !value)} edge="end">
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton></InputAdornment> }} />
          <TextField fullWidth type={showConfirmation ? 'text' : 'password'} label={t('auth.confirmPassword')}
            autoComplete="new-password" value={confirmation} onChange={event => { setConfirmation(event.target.value); setError(''); }}
            disabled={isLoading} required
            InputProps={{ endAdornment: <InputAdornment position="end"><IconButton
              aria-label={showConfirmation ? t('auth.hidePassword') : t('auth.showPassword')}
              onClick={() => setShowConfirmation(value => !value)} edge="end">
              {showConfirmation ? <VisibilityOff /> : <Visibility />}
            </IconButton></InputAdornment> }} />
          <Button type="submit" fullWidth variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : t('auth.resetPasswordButton')}
          </Button>
        </Box>
      ) : <Button component={Link} to="/forgot-password" variant="contained" fullWidth>{t('auth.forgotPassword')}</Button>}
    </AuthPageShell>
  );
};

export default ResetPassword;
